/// <reference path="../../../../node_modules/@1js/office-online-strings/en-US/transcription-strings.d.ts" />

import * as React from 'react';
import { AppButton, AppProgressBar } from '../../components';
import { Label, Link, IButton, Dropdown, IDropdownOption } from 'office-ui-fabric-react';
import { RouteComponentProps } from 'react-router-dom';
import { TranscriptionRoutePathNames } from '../../routes';
import { LogEvents } from '../../../utils/LogEvents';
import { AppLayoutSettings, getMainPageStyles } from '../../common/styles/common.styles';
import { getRecordingButtonCustomStyles, getBottomLabelStyles } from './Landing.styles';
import { getFileInputStyles, getDropdownStyles, getSupportedTranscriptionLanguages } from '../../common/pageStyles/LandingPage.styles';
import { LEARN_MORE_URL, ACCEPTED_FILE_FORMATS_FOR_UPLOAD } from '../../../AppConstants';
import { AppAuthenticationProviderContext } from '../../context';
import { SetForegroundTaskInStore } from '../../common/actions/LoadingActions';
import { FileUploadForegroundTask } from '../../../backend/fileUpload/FileUploadForegroundTask';
import { HostAppSettings, HostAppSettingName } from '../../../backend/office-js/HostAppSettings';
import { TranscriptionMonthlyQuota } from '../../../backend/transcriptionManager/TranscriptionMonthlyQuota';
import { RenderToPaneKeys } from '../../common/telemetry/RenderToPaneKeys';
import { DurationMeasureType } from '../../../backend/interfaces/ILogEvent';
import { AddTooltipToElement } from '../../components/Tooltip/AddTooltipToElement';
import { LicensePopup } from '../../components/Popups/LicensePopup';
import { AudioBackupAndRealTimeTranscriptionConnection } from '../../common/connections/AudioBackupAndRealTimeTranscriptionConnection';
import { GetDefaultTranscriptionLocale } from '../../../utils/TranscriptionLanguages';
import { GetAugloopConnectionStatus, AugloopConnectionStatus } from '../../../backend/augloop/Augloop';
import { CreateDefaultRealTimeTranscriptionConnection } from '../../../backend/factory/RealTimeTranscriptionConnectionFactory';
import { IRealTimeTranscriptionConnection } from '../../../backend/interfaces/IRealTimeTranscriptionConnection';
import { IsUnlimitedFileUploadMinutesAvailableForUser } from '../../../utils/utils';
import { LandingPageLoaded } from '../../common/actions/LandingActions';
import { TranscriptionIllustration } from '../../common/images/TranscriptionIllustration';
import { getOneNotePageInfoBar } from '../../components/OneNotePageInfoBar/OneNotePageInfoBar';
import { ShouldShowOneNotePageInfoBar } from '../../../backend/OneNote/OneNoteTranscriptionManager';

export class Landing extends React.PureComponent<RouteComponentProps<{}, {}, AudioBackupAndRealTimeTranscriptionConnection>> {
  static contextType = AppAuthenticationProviderContext;
  private recordButtonRef = React.createRef<IButton>();
  private fileInputref = React.createRef<HTMLInputElement>();

  private UploadFile(file: File) {
    LogEvents.RenderToPane.Start([{ key: RenderToPaneKeys.TriggerReason, value: "Upload audio" }]);
    LogEvents.RenderToPane.BeginDurationMeasure(DurationMeasureType.Normal, RenderToPaneKeys.DurationSecs);
    SetForegroundTaskInStore(new FileUploadForegroundTask(file, this.context, this.props.history));
    if (this.props.location.state.audioBackup != null) {
      this.props.location.state.audioBackup.Close();
      this.props.location.state.audioBackup = null;
    }

    this.props.location.state.realTimeTranscriptionConnection = null;
    this.props.history.replace(TranscriptionRoutePathNames.Loading);
  }

  public componentDidMount() {
    this.recordButtonRef.current!.focus();
    LandingPageLoaded();
  }

  public render() {
    if (!IsUnlimitedFileUploadMinutesAvailableForUser()) { // If unlimited file upload mininutes not supported
      const recordButtonID = "recordButton";
      const uploadButtonID = "uploadButton";

      const usedMinutes = TranscriptionMonthlyQuota.GetInstance().GetCachedUsedMinutes();
      const totalAvailableMinutes = TranscriptionMonthlyQuota.GetInstance().GetAvailableUploadMinutesPerMonth();
      const percentTimeUsed = TranscriptionMonthlyQuota.GetInstance().GetCachedPercentTimeUsed();
      const timeUsedLabel = usedMinutes + "/" + totalAvailableMinutes + TranscriptionStrings.LandingPageTranscriptionTimeUsedLabel;

      const styles = getMainPageStyles();

      const _onRecordClicked = (): void => {
        LogEvents.ServiceLiveSpeechToText.Start();
        let realTimeTranscriptionConnection: IRealTimeTranscriptionConnection | null = null;
        if (GetAugloopConnectionStatus() == AugloopConnectionStatus.ReadyToConnect) {
          realTimeTranscriptionConnection = CreateDefaultRealTimeTranscriptionConnection();
        } else {
          realTimeTranscriptionConnection = this.props.location.state.realTimeTranscriptionConnection;
        }
        this.props.history.replace(TranscriptionRoutePathNames.Recording, { realTimeTranscriptionConnection: realTimeTranscriptionConnection, audioBackup: this.props.location.state.audioBackup });
      };

      const _onUploadClicked = (): void => {
        this.fileInputref.current!.click();
      };

      const _onFileInput = async (event: any) => {
        LogEvents.ServiceLiveSpeechToText.Start();
        this.UploadFile(event.target.files[0]);
      };

      const enableUpload = percentTimeUsed < 1.0; // '1.0' represents 100 percent
      const uploadButton = <AppButton type={'AppButtonProps'} id={uploadButtonID} text={TranscriptionStrings.LandingPageUploadButtonLabel} onExecute={_onUploadClicked} disabled={!enableUpload} />

      const onLanguageDropdownChange = (_event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption | undefined, _index?: number | undefined): void => {
        if (option == undefined)
          return;

        HostAppSettings.Set(HostAppSettingName.DefaultTranscriptionLocale, option.key.toString());
      };

      return (
        <div style={AppLayoutSettings.AppCommonStyles.getPageRootContainersCSSProperties()}>
          {ShouldShowOneNotePageInfoBar() ? getOneNotePageInfoBar() : null}
          <input type="file" ref={this.fileInputref} data-testid="transcription-file-upload-input" style={getFileInputStyles()} onInput={_onFileInput} accept={ACCEPTED_FILE_FORMATS_FOR_UPLOAD}></input>
          <div style={{ flexGrow: 1 }}>
            <div className={styles.labelContainer}>
              <div>
                <Label styles={AppLayoutSettings.AppCommonStyles.getPageTopLabelStyles()}>{TranscriptionStrings.TranscriptionPaneDescriptionLabelWithUpload}</Label>
              </div>
            </div>
            <div className={styles.buttonContainer}>
              {(enableUpload) ?
                uploadButton
                :
                AddTooltipToElement(uploadButton, TranscriptionStrings.LandingPageUploadButtonLimitCrossedLabel)
              }
              <AppButton buttonRef={this.recordButtonRef} styles={getRecordingButtonCustomStyles()} type={'AppButtonProps'} id={recordButtonID} text={TranscriptionStrings.LandingPageRecordButtonLabel} onExecute={_onRecordClicked} />
            </div>
            <div className={styles.imageContainer}>
              <TranscriptionIllustration />
            </div>

          </div>
          <div className={styles.bottomLabelContainer}>
            <Dropdown
              defaultSelectedKey={GetDefaultTranscriptionLocale()}
              options={getSupportedTranscriptionLanguages()}
              styles={getDropdownStyles}
              onChange={onLanguageDropdownChange}
              ariaLabel={TranscriptionStrings.LangSelectAriaLabel}
            />
            <Label styles={getBottomLabelStyles()}>{timeUsedLabel}</Label>
            <div className={styles.progressContainer}>
              <AppProgressBar type={'AppProgressBarProps'} barHeight={3} percentComplete={percentTimeUsed} />
            </div>
            <Link styles={AppLayoutSettings.AppCommonStyles.getLearnMoreLinkStyles()} href={LEARN_MORE_URL} target="_blank">{TranscriptionStrings.TranscriptionPaneLearnLabel}</Link>
          </div>
          {
            HostAppSettings.IsSettingTrue(HostAppSettingName.IsFreemiumUpsellEnabled) ?
              (
                <LicensePopup />
              )
              :
              (
                null
              )
          }
        </div>
      );
    } else { // If unlimited file upload mininutes supported, user should see the updated landing page UI
      const recordButtonID = "recordButton";
      const uploadButtonID = "uploadButton";

      const usedMinutes = TranscriptionMonthlyQuota.GetInstance().GetCachedUsedMinutes();
      const percentTimeUsed = TranscriptionMonthlyQuota.GetInstance().GetCachedPercentTimeUsed();
      const timeUsedLabel = usedMinutes + TranscriptionStrings.NewLandingPageTranscriptionTimeUsedLabel;

      const styles = getMainPageStyles();

      const _onRecordClicked = (): void => {
        LogEvents.ServiceLiveSpeechToText.Start();
        this.props.history.replace(TranscriptionRoutePathNames.Recording, { realTimeTranscriptionConnection: this.props.location.state.realTimeTranscriptionConnection, audioBackup: this.props.location.state.audioBackup });
      };

      const _onUploadClicked = (): void => {
        this.fileInputref.current!.click();
      };

      const _onFileInput = async (event: any) => {
        LogEvents.ServiceLiveSpeechToText.Start();
        this.UploadFile(event.target.files[0]);
      };

      const enableUpload = percentTimeUsed < 1.0; // '1.0' represents 100 percent
      const uploadButton = <AppButton type={'AppButtonProps'} id={uploadButtonID} text={TranscriptionStrings.LandingPageUploadButtonLabel} onExecute={_onUploadClicked} disabled={!enableUpload} />

      const onLanguageDropdownChange = (_event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption | undefined, _index?: number | undefined): void => {
        if (option == undefined)
          return;

        HostAppSettings.Set(HostAppSettingName.DefaultTranscriptionLocale, option.key.toString());
      };

      return (
        <div style={AppLayoutSettings.AppCommonStyles.getPageRootContainersCSSProperties()}>
          {ShouldShowOneNotePageInfoBar() ? getOneNotePageInfoBar() : null}
          <input type="file" ref={this.fileInputref} data-testid="transcription-file-upload-input" style={getFileInputStyles()} onInput={_onFileInput} accept={ACCEPTED_FILE_FORMATS_FOR_UPLOAD}></input>
          <div style={{ flexGrow: 1 }}>
            <div className={styles.labelContainer}>
              <div>
                <Label styles={AppLayoutSettings.AppCommonStyles.getPageTopLabelStyles()}>{TranscriptionStrings.TranscriptionPaneDescriptionLabelWithUpload}</Label>
              </div>
            </div>
            <div className={styles.buttonContainer}>
              {(enableUpload) ?
                uploadButton
                :
                AddTooltipToElement(uploadButton, TranscriptionStrings.LandingPageUploadButtonLimitCrossedLabel)
              }
              <AppButton buttonRef={this.recordButtonRef} styles={getRecordingButtonCustomStyles()} type={'AppButtonProps'} id={recordButtonID} text={TranscriptionStrings.LandingPageRecordButtonLabel} onExecute={_onRecordClicked} />
            </div>
            <div className={styles.imageContainer}>
              <TranscriptionIllustration />
            </div>

          </div>
          <div className={styles.bottomLabelContainerUnlimitedMins}>
            <Dropdown
              defaultSelectedKey={GetDefaultTranscriptionLocale()}
              options={getSupportedTranscriptionLanguages()}
              styles={getDropdownStyles}
              onChange={onLanguageDropdownChange}
              ariaLabel={TranscriptionStrings.LangSelectAriaLabel}
            />
            <Label styles={AppLayoutSettings.AppCommonStyles.getTagStyles()}>{TranscriptionStrings.LandingPagePreviewLabel} </Label>
            <div className={styles.bottomMinuteLimitInfoContainer}>
              <Label styles={getBottomLabelStyles()}>{TranscriptionStrings.LandingPageTranscriptionUnlimitedMinsLabel}</Label>
              <Label styles={getBottomLabelStyles()}>{timeUsedLabel} <span aria-hidden="true" style={{ color: AppLayoutSettings.getAppColorContainer().bulletGray.value }}>&#8226;</span> <Link styles={AppLayoutSettings.AppCommonStyles.getLearnMoreLinkStyles()} href={LEARN_MORE_URL} target="_blank">{TranscriptionStrings.TranscriptionPaneLearnLabel}</Link></Label>
            </div>
          </div>
          {
            HostAppSettings.IsSettingTrue(HostAppSettingName.IsFreemiumUpsellEnabled) ?
              (
                <LicensePopup />
              )
              :
              (
                null
              )
          }
        </div>
      );
    }
  }
}